import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { format } from "date-fns"
import { Modal, Button } from "react-bootstrap"
import { toast } from "react-toastify"
import BeatLoader from "react-spinners/BeatLoader"

import { getSuggestedJobs, assignSuggestedJob } from "requesters/operationScheduleRequester"
import { getBaseUrl } from "../../reducers"
import JobSuggestionList from "./JobSuggestionList"
import { calculateAvailableCapacity } from "./utils"

// Cache duration in milliseconds (30 seconds)
const CACHE_DURATION = 30000

// In-memory cache for suggestions
const suggestionCache = new Map()

/**
 * Generate cache key for suggestions
 */
function generateCacheKey(surveyorId, date) {
  return `${surveyorId}-${format(date, "yyyy-MM-dd")}`
}

/**
 * Check if cached data is still valid
 */
function isCacheValid(cacheEntry) {
  if (!cacheEntry) return false
  const now = Date.now()
  return (now - cacheEntry.timestamp) < CACHE_DURATION
}

/**
 * JobSuggestionModal displays suggested job services for a surveyor on a specific day
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {number} props.surveyorId - Surveyor ID
 * @param {string} props.surveyorName - Surveyor name for display
 * @param {Date} props.selectedDate - Selected date
 * @param {Object} props.workingSchedule - Surveyor's working schedule
 * @param {Object} props.scheduleData - Schedule data with metrics
 * @param {Function} props.onAssignmentSuccess - Callback when assignment succeeds
 */
export default function JobSuggestionModal({
  isOpen,
  onClose,
  surveyorId,
  surveyorName,
  selectedDate,
  workingSchedule,
  scheduleData,
  onAssignmentSuccess
}) {
  const baseUrl = useSelector(getBaseUrl)

  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isAssigning, setIsAssigning] = useState(false)

  // Calculate available capacity for the selected date
  const availableCapacity = useMemo(() => {
    if (!scheduleData || !workingSchedule) return 0

    return calculateAvailableCapacity(
      surveyorId,
      selectedDate,
      workingSchedule,
      scheduleData
    )
  }, [surveyorId, selectedDate, workingSchedule, scheduleData])

  // Get target dollars from metrics
  const targetDollars = useMemo(() => {
    if (!scheduleData?.metrics) return null
    return scheduleData.metrics.target_dollars
  }, [scheduleData])

  // Fetch suggestions when modal opens
  const fetchSuggestions = useCallback(async (bypassCache = false) => {
    // Check cache first (unless bypassed)
    const cacheKey = generateCacheKey(surveyorId, selectedDate)
    const cachedEntry = suggestionCache.get(cacheKey)

    if (!bypassCache && isCacheValid(cachedEntry)) {
      setSuggestions(cachedEntry.data)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd")
      
      const result = await getSuggestedJobs(
        baseUrl,
        surveyorId,
        dateStr,
        availableCapacity,
        targetDollars
      )

      if (result.status === 200 && result.response?.data) {
        const suggestionsData = result.response.data
        setSuggestions(suggestionsData)

        // Update cache
        suggestionCache.set(cacheKey, {
          data: suggestionsData,
          timestamp: Date.now()
        })
      } else if (result.error) {
        const errorData = result.error?.response?.data
        let errorMessage = "Failed to load suggestions"
        
        if (errorData) {
          // Handle different error formats
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.title) {
            errorMessage = errorData.title
          } else if (Array.isArray(errorData.errors) && errorData.errors[0]) {
            errorMessage = typeof errorData.errors[0] === 'string' 
              ? errorData.errors[0] 
              : errorData.errors[0].detail || errorData.errors[0].title || errorMessage
          }
        } else if (result.error?.message) {
          errorMessage = result.error.message
        }
        
        setError(errorMessage)
        setSuggestions([])
      } else {
        setSuggestions([])
      }
    } catch (err) {
      console.error("Error fetching suggestions:", err)
      setError("Failed to load suggestions. Please try again.")
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }, [baseUrl, surveyorId, selectedDate, availableCapacity, targetDollars])

  // Fetch suggestions when modal opens (always bypass cache for fresh data)
  useEffect(() => {
    if (isOpen) {
      fetchSuggestions(true)
    }
  }, [isOpen, fetchSuggestions])

  // Handle job selection and assignment
  const handleSelectJob = useCallback(async (jobService) => {
    setIsAssigning(true)

    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd")
      const bookingTime = jobService.booking_time || "09:00"

      const result = await assignSuggestedJob(
        baseUrl,
        surveyorId,
        jobService.id,
        dateStr,
        bookingTime
      )

      if (result.status === 200 || result.response?.success) {
        toast.success("Job service assigned successfully")
        
        // Invalidate cache
        const cacheKey = generateCacheKey(surveyorId, selectedDate)
        suggestionCache.delete(cacheKey)

        // Call success callback
        if (onAssignmentSuccess) {
          onAssignmentSuccess()
        }
      } else if (result.error) {
        const errorData = result.error?.response?.data
        let errorMessage = "Failed to assign job service"
        
        if (errorData) {
          // Handle different error formats
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.title) {
            errorMessage = errorData.title
          } else if (Array.isArray(errorData.errors) && errorData.errors[0]) {
            errorMessage = typeof errorData.errors[0] === 'string' 
              ? errorData.errors[0] 
              : errorData.errors[0].detail || errorData.errors[0].title || errorMessage
          }
        } else if (result.error?.message) {
          errorMessage = result.error.message
        }
        
        toast.error(errorMessage)
      }
    } catch (err) {
      console.error("Error assigning job:", err)
      toast.error("Failed to assign job service. Please try again.")
    } finally {
      setIsAssigning(false)
    }
  }, [baseUrl, surveyorId, selectedDate, onAssignmentSuccess])

  // Format date for display
  const formattedDate = format(selectedDate, "EEEE, MMMM d, yyyy")

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" className="job-suggestion-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          Suggested Jobs for {surveyorName}
          <div className="modal-subtitle">
            {formattedDate} • Available: {availableCapacity.toFixed(1)}h
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <BeatLoader color="#213F7A" size={10} loading />
            <p className="mt-3 text-muted">Loading suggestions...</p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="alert alert-danger" role="alert">
            <i className="fas fa-exclamation-triangle mr-2" />
            {error}
            <button
              type="button"
              className="btn btn-sm btn-outline-danger ml-3"
              onClick={() => fetchSuggestions(true)}>
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && suggestions.length === 0 && (
          <div className="text-center py-4">
            <i className="fas fa-inbox fa-3x text-muted mb-3" />
            <p className="text-muted">No suitable job services available for this day.</p>
            <p className="text-muted small">
              Try adjusting the surveyor&apos;s schedule or check back later.
            </p>
          </div>
        )}

        {/* Suggestions List */}
        {!loading && !error && suggestions.length > 0 && (
          <JobSuggestionList
            suggestions={suggestions}
            onSelectJob={handleSelectJob}
            isAssigning={isAssigning} />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isAssigning}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

JobSuggestionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  surveyorId: PropTypes.number.isRequired,
  surveyorName: PropTypes.string.isRequired,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  workingSchedule: PropTypes.objectOf(
    PropTypes.shape({
      works: PropTypes.bool,
      start_time: PropTypes.string,
      end_time: PropTypes.string
    })
  ).isRequired,
  scheduleData: PropTypes.object,
  onAssignmentSuccess: PropTypes.func
}

JobSuggestionModal.defaultProps = {
  scheduleData: null,
  onAssignmentSuccess: null
}
